import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { staticBlogPosts, defaultBlogPostImage } from "@/pages/Blog";
import { format } from "date-fns";

export const TrendingArticles = () => {
  // Fetch database posts
  const { data: dbPosts } = useQuery({
    queryKey: ['trending-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data;
    },
  });

  // Combine database posts with static posts, limit to 6
  const allPosts = [
    ...(dbPosts || []),
    ...staticBlogPosts,
  ].slice(0, 6);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary font-medium text-sm mb-4">
            Our Blog
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Trending Articles
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Expert tips on home ownership and maintenance to protect your investment.
          </p>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {allPosts.map((post) => (
              <CarouselItem key={post.slug} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Link to={`/blog/${post.slug}`} className="group block h-full">
                  <article className="feature-card h-full flex flex-col hover:border-secondary transition-colors overflow-hidden p-0">
                    {/* Image */}
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img
                        src={post.image_url || defaultBlogPostImage}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary/10 text-secondary">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2 group-hover:text-secondary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4 flex-1 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(post.created_at), 'MMM d, yyyy')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.read_time}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </article>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center justify-center gap-4 mt-8">
            <CarouselPrevious className="static translate-y-0" />
            <CarouselNext className="static translate-y-0" />
          </div>
        </Carousel>

        <div className="text-center mt-8">
          <Link to="/blog">
            <Button variant="outline" className="font-semibold">
              View All Articles <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
